import type { FlagColor, MergeFlag } from "./types";

// ─── PE Table row & cell utilities ───────────────────────────────────────────

export type PeRow = Record<
  string,
  string | number | boolean | null | undefined
>;

function isMissingScalar(val: unknown): boolean {
  if (val === null || val === undefined) return true;
  if (typeof val === "number") return Number.isNaN(val);
  if (typeof val === "string") {
    const trimmed = val.trim();
    return (
      trimmed === "" ||
      trimmed.toLowerCase() === "nan" ||
      trimmed.toLowerCase() === "none"
    );
  }
  return false;
}

function toRowString(val: unknown): string {
  if (isMissingScalar(val)) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return "";
}

/**
 * Resolve the best display value for a key in a PeRow.
 * Priority: {key}_val → {key}_a → {key}
 */
export function getVal(row: PeRow, key: string): string {
  let v = row[`${key}_val`];
  if (isMissingScalar(v)) v = row[`${key}_a`];
  if (isMissingScalar(v)) v = row[key];
  return toRowString(v);
}

export function getQuote(row: PeRow, key: string): string {
  const q = row[`${key}_q`];
  return toRowString(q);
}

/**
 * Format a cell value for display. Applies:
 * - Currency prefix (currency before amount, e.g. "EUR 1,234,567")
 * - Percentage formatting for keys containing "pct" or "irr"
 */
export function formatCell(row: PeRow, key: string): string {
  const raw = getVal(row, key);
  if (!raw) return "—";

  const currency = toRowString(row[`${key}_curr`]).trim();
  if (currency) {
    const n = parseFloat(raw);
    if (!isNaN(n)) {
      return (
        currency +
        " " +
        new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n)
      );
    }
  }

  if ((key.includes("pct") || key.includes("irr")) && !raw.includes("%")) {
    const n = parseFloat(raw);
    if (!isNaN(n))
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 1,
      }).format(n);
  }

  return raw;
}

/** Map merge flag text → display color
 *
 * Flag values from the pipeline:
 *   "Both present and equal"     → green
 *   "Both present and differs"   → orange (conflict)
 *   "From 2, missing from 1"     → yellow (fallback has data master doesn't — notable)
 *   "From 1, missing from 2"     → none   (master only — expected, discrete)
 *   "Missing in both"            → none
 */
export function flagColor(flag: MergeFlag | undefined | null): FlagColor {
  if (!flag || flag === "" || flag === "nan") return "none";
  const f = flag.toLowerCase();
  if (f.includes("equal")) return "green";
  if (f.includes("differs") || f.includes("differ")) return "orange";
  // "From 2, missing from 1": fallback has something master doesn't → highlight
  if (f.startsWith("from 2") || f.includes("missing from 1")) return "yellow";
  // "From 1, missing from 2": master only — expected, no colour
  return "none";
}

export const FLAG_BG: Record<FlagColor, string> = {
  green: "bg-green-100 text-green-900",
  orange: "bg-orange-100 text-orange-900",
  yellow: "bg-yellow-100 text-yellow-900",
  none: "",
};

export const FLAG_BORDER: Record<FlagColor, string> = {
  green: "border-green-300",
  orange: "border-orange-300",
  yellow: "border-yellow-300",
  none: "border-transparent",
};

/**
 * Format a numeric string with thousands separators.
 * Handles values like "9961.00", "nan", "", "-1234.5"
 */
export function fmtNum(
  val: string | undefined | null,
  suffix?: string,
): string {
  if (!val || val === "nan" || val === "") return "—";
  const n = parseFloat(val.replace(/,/g, ""));
  if (isNaN(n)) return val;
  const formatted = n.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  return suffix ? `${formatted} ${suffix}` : formatted;
}

/**
 * Format a percentage string. Input is usually already a decimal (e.g. "7.5")
 * or sometimes a fraction (e.g. "0.075"). Template uses raw string display.
 */
export function fmtPct(val: string | undefined | null): string {
  if (!val || val === "nan" || val === "") return "—";
  return val;
}

/**
 * Extracts the best search term from an LLM citation string.
 * E.g. "Total commitments 9,961 [p. 8]" → "9,961"
 * Mirrors extractSearchTerm() from template.html
 */
export function extractSearchTerm(q: string | null | undefined): string | null {
  if (!q || q === "nan" || q === "") return null;
  const text = q.replace(/\[p\.\s*[\d,\s-]+\]/gi, "").trim();
  const nums = text.match(/-?[\d][\d,.\s]*[\d]%?/g);
  if (nums) {
    const candidates = nums.filter(
      (n) => n.replace(/[,.\s%-]/g, "").length >= 3,
    );
    if (candidates.length > 0) {
      candidates.sort((a, b) => b.length - a.length);
      return candidates[0].trim();
    }
  }
  return text.substring(0, 60).trim() || null;
}

/** Extract page number from citation string "[p. 12]" → 12 */
export function extractPage(q: string | null | undefined): number | null {
  if (!q) return null;
  const m = q.match(/\[p\.\s*([\d]+)/i);
  return m ? parseInt(m[1], 10) : null;
}

/** True if a string value is considered empty/missing */
export function isEmpty(val: unknown): boolean {
  return isMissingScalar(val);
}

/**
 * Parse a reporting period string into a sortable numeric key.
 * Mirrors parse_temporal_keys() from the Python pipeline.
 *
 * Examples:
 *   Q1_2024  → [2024, 1]
 *   Q3_2024  → [2024, 3]
 *   H1_2024  → [2024, 2]   (H1 = end of Q2)
 *   H2_2024  → [2024, 4]
 *   HY1_2024 → [2024, 2]
 *   A2024    → [2024, 4]   (Annual = end of Q4)
 *   ANNUAL_2024 → [2024, 4]
 *   YTD_2024 → [2024, 3]   (YTD usually up to Q3)
 */
export function parsePeriodKey(period: string): [number, number] {
  const s = period.toUpperCase();
  const yearMatch = s.match(/(20\d{2})/);
  const year = yearMatch ? parseInt(yearMatch[1], 10) : 1900;

  let q = 0;
  if (s.includes("Q1")) q = 1;
  else if (s.includes("Q2") || s.includes("H1") || s.includes("HY1")) q = 2;
  else if (s.includes("Q3") || s.includes("YTD")) q = 3;
  else if (
    s.includes("Q4") ||
    s.includes("H2") ||
    s.includes("HY2") ||
    s.includes("ANNUAL") ||
    /\bA\d{4}/.test(s) ||
    s.startsWith("A2")
  )
    q = 4;

  return [year, q];
}

/** Sort an array of reporting period strings chronologically. */
export function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((a, b) => {
    const [ya, qa] = parsePeriodKey(a);
    const [yb, qb] = parsePeriodKey(b);
    return ya !== yb ? ya - yb : qa - qb;
  });
}

/**
 * Convert a reporting_period string to a canonical grouping key "YYYY_Q".
 * "A2024" and "Q42024" both → "2024_4" (annual = Q4).
 * Unparseable periods → "0_0".
 */
export function periodKey(period: string): string {
  const [y, q] = parsePeriodKey(period);
  return `${y}_${q}`;
}

/**
 * Find the doc_id from a list that matches a given period key.
 * Returns null if no match found.
 */
export function findDocForKey(
  docIds: string[],
  key: string,
  docMeta: Record<string, { reporting_period: string }>,
): string | null {
  for (const docId of docIds) {
    const p = docMeta[docId]?.reporting_period;
    if (p && periodKey(p) === key) return docId;
  }
  return null;
}
